// Comprehensive error logging service for Orbit
//
// SCALE-2 — `debug.error` is the closest thing this codebase has to a
// central failure funnel, so it is now also the cheapest place to make
// a swallowed error REPORTABLE. Every existing call site gets error
// tracking without being touched. `debug.warn`/`log`/`success` stay
// console-only: they're not failures and they'd eat the quota.
//
// Everything handed to Sentry goes through `captureAppError`, which
// scrubs before send — see src/lib/observability.ts. `context.payload`
// in particular routinely contains whole database rows (client emails,
// contract bodies), which is exactly why it is scrubbed rather than
// forwarded.
import { captureAppError } from '@/lib/observability';

const isDev = import.meta.env.DEV;

interface DebugContext {
  userId?: string;
  orgId?: string;
  action: string;
  payload?: unknown;
  table?: string;
}

export const debug = {
  log: (message: string, data?: unknown) => {
    if (isDev) {
      console.log(`[Orbit] ${message}`, data ?? '');
    }
  },

  warn: (message: string, data?: unknown) => {
    console.warn(`[Orbit] ⚠️ ${message}`, data ?? '');
  },

  error: (context: DebugContext, error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorDetails = error instanceof Error ? error : { message: errorMessage };
    
    console.error(`[Orbit] ❌ ${context.action} failed`, {
      ...context,
      error: errorDetails,
      timestamp: new Date().toISOString(),
    });

    // In dev mode, also show a more detailed console group
    if (isDev) {
      console.group(`🔴 Error Details: ${context.action}`);
      console.log('User ID:', context.userId ?? 'N/A');
      console.log('Org ID:', context.orgId ?? 'N/A');
      console.log('Table:', context.table ?? 'N/A');
      console.log('Payload:', context.payload ?? 'N/A');
      console.log('Error:', errorDetails);
      console.groupEnd();
    }

    // SCALE-2 — hand it to the error tracker. `table` and `action` are
    // safe identifiers; `payload` is NOT, so it goes through the
    // scrubber like everything else. userId/orgId are opaque uuids and
    // are the whole point — they turn "someone hit an error" into "one
    // studio hit an error 40 times".
    captureAppError(error, {
      op: context.action,
      extra: {
        table: context.table,
        user_id: context.userId,
        org_id: context.orgId,
        payload: context.payload,
      },
    });

    return errorMessage;
  },

  success: (action: string, data?: unknown) => {
    if (isDev) {
      console.log(`[Orbit] ✅ ${action}`, data ?? '');
    }
  },

  // Helper to extract meaningful error messages from Supabase errors
  parseSupabaseError: (error: unknown): string => {
    if (!error) return 'Unknown error';
    
    if (typeof error === 'object' && error !== null) {
      const err = error as Record<string, unknown>;
      
      // Supabase error format
      if (err.message) {
        const message = String(err.message);
        
        // Common error translations
        if (message.includes('duplicate key')) {
          return 'This record already exists';
        }
        if (message.includes('violates row-level security')) {
          return 'Permission denied. Please ensure you are logged in and have access.';
        }
        if (message.includes('violates foreign key')) {
          return 'Referenced record not found';
        }
        if (message.includes('violates not-null')) {
          return 'Required field is missing';
        }
        
        return message;
      }
      
      if (err.error_description) {
        return String(err.error_description);
      }
    }
    
    return String(error);
  },
};

export default debug;
